import React, { useState, useEffect } from "react";
import "@vapi-ai/web";
import VapiWidgetComponent from '../components/VapiWidgetComponent';
import { Link } from 'react-router-dom';
import { ArrowLeft, Target, Zap, Users, Globe, X } from 'lucide-react';
import Footer from '../components/Footer';
import { supabase } from '../SupabaseClient';
import NS from "../assets/NS_mini_Logo.png";
import { message } from 'antd';

function About() {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: "",
        businessName: "",
        businessType: "",
        email: "",
        phone: "",
        service: "",
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const { data, error } = await supabase
                .from('leads') // Replace with your table name
                .insert([
                    {
                        name: form.name,
                        business_name: form.businessName,
                        business_type: form.businessType,
                        email: form.email,
                        phone: form.phone,
                        service: form.service,
                        created_at: new Date().toISOString()
                    }
                ]);

            if (error) throw error;

            console.log("Form submitted successfully:", data);
            message.success("Thank you! We'll be in touch soon.");

            // Reset form
            setForm({
                name: "",
                businessName: "",
                businessType: "",
                email: "",
                phone: "",
                service: "",
            });

            setOpen(false);
        } catch (error) {
            console.error("Error submitting form:", error);
            message.error("There was an error submitting the form. Please try again.");
        }
    };
    return (
        <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
            <nav className="sticky top-0 z-50 px-6 py-6 backdrop-blur-sm border-b border-slate-800">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
                        <ArrowLeft className="w-5 h-5 text-blue-400" />
                        <span className="text-sm font-semibold text-slate-400">Back</span>
                    </Link>
                    <div className="flex items-center space-x-2">
                        <img src={NS} width="125px" alt="NeuroStudio Logo" />
                    </div>
                    <div className="w-20" />
                </div>
            </nav>

            <section className="relative py-10 px-6">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-20">
                        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                            About
                            <br />
                            <span className="bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
                                NeuroStudio
                            </span>
                        </h1>
                        <p className="text-xl text-slate-300 max-w-2xl mx-auto">
                            Redefining how businesses leverage AI to drive growth and transform operations
                        </p>
                    </div>

                    <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-12 md:p-16 mb-16">
                        <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
                        <p className="text-lg text-slate-300 leading-relaxed mb-6">
                            At NeuroStudio, we believe that artificial intelligence should be accessible to every business, regardless of size or technical expertise. Our mission is to democratize AI by providing comprehensive solutions that empower companies to automate workflows, engage customers intelligently, and scale operations exponentially.
                        </p>
                        <p className="text-lg text-slate-300 leading-relaxed">
                            We're not just building tools; we're creating digital workforces that work alongside your team, making business more efficient, more human-centered, and more innovative.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 mb-16">
                        <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-12">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6">
                                <Target className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
                            <p className="text-slate-300 leading-relaxed">
                                A world where AI is seamlessly integrated into business operations, enabling companies to focus on innovation and growth while intelligent systems handle the rest.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-12">
                            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mb-6">
                                <Zap className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-4">Our Approach</h3>
                            <p className="text-slate-300 leading-relaxed">
                                We combine cutting-edge AI technology with thoughtful design and genuine customer care. Every solution is built to scale, secure, and simple to implement.
                            </p>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-12 md:p-16 mb-16">
                        <h2 className="text-3xl font-bold text-white mb-12">Why Choose NeuroStudio?</h2>
                        <div className="grid md:grid-cols-3 gap-12">
                            <div>
                                <div className="w-14 h-14 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mb-6">
                                    <Users className="w-7 h-7 text-blue-400" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-3">Expert Team</h3>
                                <p className="text-slate-400">
                                    Our team brings decades of combined experience in AI, automation, and digital design.
                                </p>
                            </div>
                            <div>
                                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                                    <Target className="w-7 h-7 text-emerald-400" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-3">Proven Results</h3>
                                <p className="text-slate-400">
                                    Our clients see measurable improvements in efficiency, engagement, and revenue within months.
                                </p>
                            </div>
                            <div>
                                <div className="w-14 h-14 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl flex items-center justify-center mb-6">
                                    <Globe className="w-7 h-7 text-orange-400" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-3">Global Support</h3>
                                <p className="text-slate-400">
                                    Available 24/7 across all time zones to support your business wherever you operate.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="text-center">
                        <h2 className="text-3xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
                        <button
                            className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-emerald-500 text-white rounded-full font-semibold hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 transform hover:scale-105"
                            onClick={() => setOpen(true)}
                        >
                            <span>Get Started</span>
                            <ArrowLeft className="w-5 h-5 rotate-180" />
                        </button>
                    </div>
                </div>
            </section>

            {/* Modal */}
            {open && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 sm:p-8 w-full max-w-lg relative shadow-2xl max-h-[90vh] overflow-y-auto">
                        <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-white hover:text-red-400">
                            <X className="w-6 h-6" />
                        </button>

                        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 text-center">Start Your AI Journey</h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <input
                                type="text"
                                name="name"
                                placeholder="Your Name"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.name}
                                onChange={handleChange}
                                required
                            />

                            <input
                                type="text"
                                name="businessName"
                                placeholder="Business Name"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.businessName}
                                onChange={handleChange}
                            />

                            <input
                                type="text"
                                name="businessType"
                                placeholder="Business Type"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.businessType}
                                onChange={handleChange}
                            />

                            <input
                                type="email"
                                name="email"
                                placeholder="Email (required)"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.email}
                                onChange={handleChange}
                                required
                            />

                            <input
                                type="tel"
                                name="phone"
                                placeholder="Phone Number (optional)"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.phone}
                                onChange={handleChange}
                            />

                            <select
                                name="service"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white"
                                value={form.service}
                                onChange={handleChange}
                                required
                            >
                                <option value="" className="text-slate-900">Select a Service</option>
                                <option value="AI Agent">AI Agent / Employee</option>
                                <option value="Chatbot">Chatbot</option>
                                <option value="Backend Automation">Backend Automation</option>
                                <option value="Web/App Design">Web / App Design</option>
                                <option value="Custom Suite">Custom Suite</option>
                            </select>

                            <button
                                type="submit"
                                className="w-full py-3 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-lg text-white font-semibold hover:opacity-90"
                            >
                                Submit
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <Footer />
            <VapiWidgetComponent />
        </div>
    )
}

export default About